import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShieldCheck, Users, HeartHandshake, Sparkles, Globe, Target, Eye, Compass } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/images";

export const STATEMENTS = [
  { icon: Globe, label: "Global Vision", text: "A world where no one feels so alone in their pain that they lose sight of tomorrow — where every story, however dark its chapter, is met with light, dignity, and hope." },
  { icon: Target, label: "Mission Statement", text: "To help people find support, rebuild hope, and reach trusted resources — offering a refined, judgment-free sanctuary of stories, tools, and human connection that reminds every person their life matters." },
  { icon: Eye, label: "Vision", text: "To become a trusted global beacon for resilience and suicide prevention, reaching hearts in every community with compassion, creativity, and unwavering hope." },
  { icon: Compass, label: "Purpose", text: "To use the gifts and talents we have been given to serve people wholeheartedly — and to live as true humanitarians, turning compassion into action." },
];

const VALUES = [
  { icon: Heart, title: "Unconditional Welcome", text: "Every person who arrives is met without judgment, exactly as they are, in whatever chapter they're living." },
  { icon: ShieldCheck, title: "Privacy First", text: "We practice strict confidentiality and zero data-selling. Your trust is sacred and carefully protected." },
  { icon: Users, title: "Lived-Experience Led", text: "Our stories, tools, and tone are shaped by survivors and allies who have walked these roads." },
  { icon: HeartHandshake, title: "Humanitarian Heart", text: "We turn compassion into action — using our gifts and talents to serve people wholeheartedly." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About BSN"
        title="Born from the belief that no one should face the dark alone"
        subtitle="Bright Stories Network exists to accompany people through their hardest moments — with dignity, warmth, and unwavering hope."
        image={IMG.caringHands}
        testid="about-hero"
      />

      <section className="max-w-3xl mx-auto px-4 py-16 sm:py-20">
        <Reveal>
          <Sparkles className="w-8 h-8 text-bsn-gold mb-6" />
          <h2 className="font-serif text-3xl font-semibold text-bsn-midnight leading-snug">Our Story</h2>
          <div className="mt-5 space-y-4 text-bsn-sapphire/85 leading-relaxed text-lg">
            <p>
              Bright Stories Network began with a simple, stubborn conviction: that every life holds a story
              still worth telling, and that hope — however faint — can be rebuilt. We saw too many people slip
              through the cracks in silence, and we decided to build a place that reaches back.
            </p>
            <p>
              We are not a crisis hotline. We are the warm room you can step into afterward — to find trusted
              resources, hear from others who survived, calm your nervous system, and remember that staying is
              an act of quiet courage.
            </p>
            <p>
              From the Calming Media Sanctuary to Super Kids bookmarks, every corner of this network is designed
              to meet people where they are and walk beside them toward morning.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-bsn-canvas border-y border-bsn-gold/20">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <SectionTitle eyebrow="How We Live It" title="Core Values" />
          <div className="grid sm:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 80}>
                <div className="bg-white rounded-2xl p-7 card-tactile flex gap-5">
                  <div className="w-12 h-12 rounded-xl bg-bsn-gold/15 flex items-center justify-center shrink-0">
                    <v.icon className="w-6 h-6 text-bsn-bronze" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-bsn-midnight">{v.title}</h3>
                    <p className="text-sm text-bsn-sapphire/80 mt-2 leading-relaxed">{v.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-14" data-testid="about-legal">
        <div className="bg-white rounded-2xl p-7 sm:p-8 card-tactile border-l-4 border-bsn-gold flex flex-col sm:flex-row sm:items-center gap-5">
          <ShieldCheck className="w-10 h-10 text-bsn-bronze shrink-0" />
          <div className="flex-1">
            <h3 className="font-serif text-2xl font-semibold text-bsn-midnight">Privacy &amp; Terms</h3>
            <p className="text-sm text-bsn-sapphire/80 mt-1.5 leading-relaxed">How we protect your information, and the simple terms for using Bright Stories Network.</p>
          </div>
          <Link to="/legal" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-bsn-midnight text-bsn-parchment font-semibold text-sm shrink-0" data-testid="about-legal-link">
            Read Privacy &amp; Terms
          </Link>
        </div>
      </section>
    </div>
  );
}
